import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import UpdateImportSettingsDialog from "@/pages/(app)/integrations/components/import-settings/update-import-dialog.tsx";
import useDeleteImportMethod from "@babackapp/private-api-client/client/use-delete-import-model";
import useExecuteImportJob from "@babackapp/private-api-client/client/use-execute-import-job";
import useGetImportTasks from "@babackapp/private-api-client/client/use-get-import-tasks";
import { ShopImportDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import SimpleMenu from "@babackapp/ui/components/simple-menu";
import { TrashIcon } from "@heroicons/react/24/solid";
import { SwitchIcon } from "@radix-ui/react-icons";
import { formatDistanceToNow } from "date-fns";
import {
  DatabaseIcon,
  MoreHorizontal,
  Pencil,
  RefreshCwIcon,
} from "lucide-react";
import { useState } from "react";
import AddImportDialog from "./add-import-dialog";
import Task from "./task";

export default function ImportSettings() {
  const t = useTranslation();
  const hasAdminRights = useViewerHasAtLeastAdminRights();
  const { data: shop } = useCurrentShopSuspense();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <CardTitle>{t("settings.integrations.imports.title")}</CardTitle>
            <CardDescription>
              {t("settings.integrations.imports.subtitle")}
            </CardDescription>
          </div>
          {hasAdminRights && (
            <AddImportDialog>
              <Button>{t("settings.integrations.imports.add")}</Button>
            </AddImportDialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6 pt-6">
          {shop.imports.map((importConfig) => {
            return (
              <FTPImport key={importConfig.jobId} importConfig={importConfig} />
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function FTPImport(props: { importConfig: ShopImportDto }) {
  const { data: shop } = useCurrentShopSuspense();
  const [isExecuting, setIsExecuting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [tasksIsOpen, setTasksIsOpen] = useState(false);
  const t = useTranslation();
  const { data: tasks } = useGetImportTasks(shop.id, props.importConfig.jobId);

  const { mutateAsync: reRunTask } = useExecuteImportJob(shop);
  const { mutateAsync: deleteTask } = useDeleteImportMethod(shop);

  const handleDeleteTask = async () => {
    try {
      await deleteTask({ importId: props.importConfig.jobId });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="flex justify-between group">
        <div className="flex space-x-4 text-sm">
          <div className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
            <div className="-ml-0.5 mr-1.5 h-1.5 w-1.5 bg-green-400 rounded-full"></div>
            {props.importConfig.type.toUpperCase()}
          </div>
          <div className="">{props.importConfig.login.host}</div>
          <div className="text-gray-500">
            {`${t("settings.integrations.imports.lastRun")} ${
              props.importConfig.lastRun > 0
                ? formatDistanceToNow(new Date(props.importConfig.lastRun), {
                    addSuffix: true,
                  })
                : t("misc.never")
            }`}
          </div>
        </div>
        {isExecuting && (
          <div className="flex items-center animate-spin text-gray-500">
            <RefreshCwIcon className="flex h-4 w-4 text-gray-400 -scale-y-100" />
          </div>
        )}
        <SimpleMenu
          iconTitle={<MoreHorizontal className="h-4 w-4" />}
          type={"neutral"}
          menu={[
            {
              title: "",
              onClick: async () => {
                if (isExecuting) {
                  return;
                }
                setIsExecuting(true);
                try {
                  await reRunTask(props.importConfig.jobId);
                } finally {
                  setIsExecuting(false);
                }
              },
              renderTitle: () => (
                <p>{t("settings.integrations.webhooks.menu.test")}</p>
              ),
              icon: <SwitchIcon className="h-4 w-4 mr-1" />,
            },
            {
              onClick: () => {
                setIsUpdating(true);
              },
              title: t("settings.integrations.webhooks.menu.edit"),
              icon: <Pencil className="h-4 w-4 mr-1" />,
            },
            {
              onClick: () => {
                setTasksIsOpen(!tasksIsOpen);
              },
              title: tasksIsOpen
                ? t("settings.integrations.imports.hideTasks")
                : t("settings.integrations.imports.showTasks"),
              icon: <DatabaseIcon className="h-4 w-4 mr-1" />,
            },
            {
              onClick: async () => {
                setIsDeleting(true);
              },
              renderTitle: () => (
                <p className="text-red-500">{t("misc.delete")}</p>
              ),
              icon: <TrashIcon className="h-4 w-4 mr-1 text-red-500" />,
            },
          ]}
        />
      </div>
      {tasksIsOpen && (
        <div className="space-y-2">
          {tasks?.map((task) => <Task key={task.id} task={task} shop={shop} />)}
        </div>
      )}

      {isUpdating && (
        <UpdateImportSettingsDialog
          shopImport={props.importConfig}
          open={isUpdating}
          onOpenChange={setIsUpdating}
        />
      )}

      {isDeleting && (
        <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("settings.integrations.imports.removeImport.title")}
              </DialogTitle>
              <DialogDescription>
                {t("settings.integrations.imports.removeImport.description")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex space-x-2 justify-end">
              <Button variant="outline" onClick={() => setIsDeleting(false)}>
                {t("misc.cancel")}
              </Button>
              <Button variant="destructive" onClick={() => handleDeleteTask()}>
                {t("misc.delete")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
